import React, { useState } from 'react';
import { FieldLabel, Fields } from '@measured/puck';
import { Select } from '../../../page-builder/components/form-fields/Select';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Radio } from '../../../page-builder/components/form-fields/Radio';
import CustomTooltip from '../../../components/common/tooltip';

// Define the types for ImageProps and Fields
type BackgroundImageSubProps = {
  backgroundRepeat: string;
  backgroundSize: string;
  backgroundPosition: string;
};

export interface BackgroudImageProps {
  bg_image: BackgroundImageSubProps;
}

interface Option {
  label: string;
  value: string;
}
interface RadioOption {
  id: string;
  name: string;
  value: boolean;
}

interface SelectField {
  type: 'select';
  label: string;
  options: Option[];
}

interface BackgroundImageField {
  backgroundRepeat: SelectField;
  backgroundSize: SelectField;
  backgroundPosition: SelectField;
}

export const backgroundImageProps = (type: string[]) => {
  const imageField: BackgroundImageField = {
    backgroundRepeat: {
      type: 'select',
      label: 'Background Repeat',
      options: [
        { label: 'No Repeat', value: 'bg-no-repeat' },
        { label: 'Repeat X', value: 'bg-repeat-x' },
        { label: 'Repeat Y', value: 'bg-repeat-y' },
        { label: 'Repeat', value: 'bg-repeat' },
      ],
    },
    backgroundSize: {
      type: 'select',
      label: 'Background Size',
      options: [
        { label: 'Auto', value: 'bg-auto' },
        { label: 'Cover', value: 'bg-cover' },
        { label: 'Contain', value: 'bg-contain' },
      ],
    },
    backgroundPosition: {
      type: 'select',
      label: 'Background Position',
      options: [
        { label: 'Center', value: 'bg-center' },
        { label: 'Top', value: 'bg-top' },
        { label: 'Right', value: 'bg-right' },
        { label: 'Bottom', value: 'bg-bottom' },
        { label: 'Left', value: 'bg-left' },
        { label: 'Top Right', value: 'bg-top-right' },
        { label: 'Top Left', value: 'bg-top-left' },
        { label: 'Bottom Right', value: 'bg-bottom-right' },
        { label: 'Bottom Left', value: 'bg-bottom-left' },
      ],
    },
  };

  if (type.length > 0) {
    type.forEach(key => {
      delete imageField[key as keyof BackgroundImageField];
    });
  }

  return {
    bg_image: {
      type: 'custom',
      label: 'Image',
      render: ({ value, onChange }) => {
        const [isOpen, setIsOpen] = useState(false);

        const toggleAccordion = () => {
          setIsOpen(!isOpen);
        };

        const handleChange = (key: string, option: string | number) => {
          const updatedValue = option;

          onChange({
            ...value,
            [key]: updatedValue,
          });
        };

        return (
          <div>
            <div
              className="flex justify-between font-bold cursor-pointer bg-slate-100 rounded-md pagebuilder-theme"
              onClick={toggleAccordion}>
              <h3>Customize Background Image</h3>
              {!isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
            </div>
            {isOpen && (
              <div className="flex flex-col mt-3 items-center border border-gray-300 rounded p-2 w-full pagebuilder-theme">
                {Object.entries(imageField).map(([field, config]) => (
                  <div key={field} className="p-2 w-full">
                    {config.type === 'select' ? (
                      <FieldLabel
                        label={
                          (
                            <CustomTooltip title={config?.label}>
                              <span>{config.label}</span>
                            </CustomTooltip>
                          ) as React.ReactNode as string
                        }>
                        <Select
                          value={
                            value?.[field as keyof BackgroundImageSubProps]
                          }
                          onChange={e => handleChange(field, e.target.value)}
                          options={config.options || []}
                        />
                      </FieldLabel>
                    ) : (
                      <Radio
                        value={value?.[field as keyof BackgroundImageSubProps]}
                        onChange={e => handleChange(field, e.target.value)}
                        options={config.options as RadioOption[]}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      },
      defaultItemProps: {
        backgroundPosition: 'bg-center',
        backgroundRepeat: 'bg-no-repeat',
        backgroundSize: 'bg-cover',
      },
    },
  } as Fields<BackgroudImageProps>;
};
